/**
 * 2026-09-20 — Kite (Zerodha) equivalent of UpstoxAutoLoginPanel.jsx (chat:
 * "Need to implement TOTP for Zerodha", after diagnosing why "Test
 * Connection" failed post-credential-update: kite_service.py's real
 * connection is driven entirely by env vars + a manual "Login with
 * Zerodha" OAuth click — there was no TOTP automation for Kite at all).
 *
 * This is a SEPARATE, global, admin-configured auto-login store
 * (/api/kite/auto-login/*) — independent of the "Zerodha API Credentials"
 * panel above it on this same page, which only records api_key/api_secret/
 * user_id/password/totp_secret for Phase-1 audit purposes and isn't wired
 * to any login flow (see that panel's own note). Same relationship
 * UpstoxAutoLoginPanel has to the "Upstox API Credentials" panel.
 *
 * No debug-screenshot section here (unlike Upstox's panel) — the Kite
 * login flow is plain HTTP (see kite_autologin.py's module docstring for
 * why no browser is needed), so there's no page to screenshot; failures
 * come back as a plain error string from the relevant HTTP step instead.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const KiteAutoLoginPanel = ({ onRefresh, hidden = false }) => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ user_id: "", password: "", totp_secret: "" });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/kite/auto-login/status`);
      setStatus(r.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (hidden) return null;

  const save = async () => {
    if (!form.user_id || !form.password || !form.totp_secret) {
      toast.error("All three fields are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/kite/auto-login/config`, form);
      toast.success("Kite auto-login configured");
      setForm({ user_id: "", password: "", totp_secret: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kite/auto-login/run`);
      if (r.data?.ok) {
        toast.success(`Kite auto-login succeeded${r.data.user_name ? ` · ${r.data.user_name}` : ""}`);
      } else {
        toast.error(r.data?.error || "Auto-login failed");
      }
      await load();
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Auto-login failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Remove stored Zerodha auto-login credentials?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/kite/auto-login/config`);
      toast.success("Auto-login cleared");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const configured = !!status?.configured;
  const lastRunOk = status?.last_run_ok;

  return (
    <div className="border-grid surface" data-testid="kite-autologin-panel">
      <button
        data-testid="kite-autologin-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02]"
      >
        <span className="text-[11px] uppercase tracking-wider font-mono-data text-muted-2 inline-flex items-center gap-2">
          <RefreshCw size={13} />
          Zerodha Auto-Login (TOTP)
          <span
            className="text-[9px] px-1.5 py-0.5 border ml-2"
            style={{
              color: configured ? (lastRunOk === false ? "#FFB66E" : "var(--bull)") : "var(--text-muted)",
              borderColor: configured ? (lastRunOk === false ? "#FFB66E" : "var(--bull)") : "var(--border-color)",
            }}
          >
            {configured ? (lastRunOk === false ? "LAST RUN FAILED" : "CONFIGURED") : "NOT CONFIGURED"}
          </span>
          {status?.user_id_masked && (
            <span className="text-muted-3">· {status.user_id_masked}</span>
          )}
        </span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="border-t border-grid px-4 py-4 space-y-3">
          <p className="text-[11px] text-muted-2 leading-relaxed">
            Auto-login replays Zerodha's own login + TOTP session flow at{" "}
            <strong>08:30 IST daily</strong> (well after Kite's ~07:30 IST token-invalidation
            window and before the 09:15 market open), so the access token refreshes without
            a manual "Login with Zerodha" click. Credentials are encrypted at rest using a
            key derived from <code>KITE_API_SECRET</code>.
          </p>
          <div className="border surface px-3 py-2 text-[10px]"
            style={{ borderColor: "#FFB66E", background: "rgba(255,182,110,0.06)", color: "#FFB66E" }}
          >
            <strong>⚠ Security note:</strong> your Zerodha login password and TOTP secret will
            be stored on this server. To use this you must first enable External TOTP 2FA on
            your Zerodha profile (Console → Password &amp; Security) and copy the secret key
            shown there (not the 6-digit code) — same value as the "TOTP Secret" field above,
            but saved to a separate store that's actually wired to this job.
          </div>

          {!configured && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">Zerodha User ID</div>
                  <input
                    data-testid="kite-autologin-userid"
                    type="text"
                    autoComplete="off"
                    value={form.user_id}
                    onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                    className="w-full border-grid surface px-2 py-1.5 text-xs font-mono-data"
                    placeholder="AB1234"
                  />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">Zerodha Password</div>
                  <input
                    data-testid="kite-autologin-password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full border-grid surface px-2 py-1.5 text-xs font-mono-data"
                    placeholder="••••••••"
                  />
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-wider text-muted-3 mb-1 font-mono-data">TOTP Secret (Base32)</div>
                  <input
                    data-testid="kite-autologin-totp"
                    type="password"
                    autoComplete="new-password"
                    value={form.totp_secret}
                    onChange={(e) => setForm({ ...form, totp_secret: e.target.value })}
                    className="w-full border-grid surface px-2 py-1.5 text-xs font-mono-data"
                    placeholder="JBSWY3DPEHPK3PXP"
                  />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="kite-autologin-save"
                  onClick={save}
                  disabled={busy}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5 disabled:opacity-50"
                >
                  Save Credentials
                </button>
              </div>
            </>
          )}

          {configured && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="kite-autologin-run"
                onClick={runNow}
                disabled={busy}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1.5"
                style={{ borderColor: "var(--bull)", color: "var(--bull)" }}
              >
                <RefreshCw size={11} className={busy ? "animate-spin" : ""} />
                {busy ? "Logging in…" : "Run Auto-Login Now"}
              </button>
              <button
                data-testid="kite-autologin-clear"
                onClick={clear}
                disabled={busy}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border border-grid hover:bg-white/5 disabled:opacity-50"
                style={{ color: "var(--bear)" }}
              >
                Remove Credentials
              </button>
              {status?.last_run_at && (
                <span className="text-[10px] text-muted-3 font-mono-data ml-auto">
                  Last run: {(status.last_run_at || "").slice(11, 16)} IST · {lastRunOk ? "✓" : "✗"}
                  {status.last_run_error && (
                    <span className="ml-2 text-muted-2" title={status.last_run_error}>
                      ({status.last_run_error.slice(0, 60)}…)
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KiteAutoLoginPanel;
